import * as React from 'react';
import { ClockIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

export type TimePickerProps = {
  /** Local time as `HH:mm`, 00:00–23:59. */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  id?: string;
  'aria-label'?: string;
  label?: string;
  className?: string;
  placeholder?: string;
  /** Raise the portalled time list above a full-screen dialog. */
  positionerClassName?: string;
};

const HOURS = Array.from({ length: 24 }, (_, hour) =>
  String(hour).padStart(2, '0')
);
const MINUTES = Array.from({ length: 60 }, (_, minute) =>
  String(minute).padStart(2, '0')
);

function TimePicker({
  value,
  onChange,
  disabled,
  id,
  'aria-label': ariaLabel,
  label,
  className,
  placeholder = 'HH:mm',
  positionerClassName,
}: TimePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState<string | null>(null);
  const generatedId = React.useId();
  const inputId = id ?? generatedId;
  const [hour = '', minute = ''] = value.split(':');

  const select = (nextHour: string, nextMinute: string) => {
    setDraft(null);
    onChange(`${nextHour.padStart(2, '0')}:${nextMinute.padStart(2, '0')}`);
  };

  const commit = (text: string) => {
    const trimmed = text.trim();
    setDraft(null);
    if (!trimmed) {
      onChange('');
      return;
    }
    const parsed = parseTypedTime(trimmed);
    if (parsed) onChange(parsed);
  };

  return (
    <div
      className={cn(
        'tw:inline-flex tw:w-fit tw:flex-col tw:gap-1.5',
        className
      )}
    >
      {label ? <Label htmlFor={inputId}>{label}</Label> : null}
      <div className="tw:inline-flex tw:items-center tw:gap-1">
        <Input
          id={inputId}
          value={draft ?? value}
          placeholder={placeholder}
          disabled={disabled}
          aria-label={ariaLabel ?? label}
          inputMode="numeric"
          onChange={(event) => setDraft(event.target.value)}
          onBlur={(event) => commit(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            event.preventDefault();
            commit(event.currentTarget.value);
          }}
          className="tw:w-20 tw:tabular-nums"
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger
            render={
              <Button
                type="button"
                variant="outline"
                size="icon"
                disabled={disabled}
                aria-label="Open time list"
              />
            }
          >
            <ClockIcon className="tw:size-4" />
          </PopoverTrigger>
          <PopoverContent
            className="dark tw:w-auto tw:p-0"
            positionerClassName={positionerClassName}
            align="start"
          >
            <div className="tw:flex tw:divide-x tw:divide-border">
              <TimeColumn
                label="Hour"
                options={HOURS}
                selected={hour}
                onSelect={(nextHour) => select(nextHour, minute || '00')}
              />
              <TimeColumn
                label="Min"
                options={MINUTES}
                selected={minute}
                onSelect={(nextMinute) => select(hour || '00', nextMinute)}
              />
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}

function parseTypedTime(text: string): string | undefined {
  const clock = /^(\d{1,2}):(\d{2})$/.exec(text);
  if (clock) return militaryTime(Number(clock[1]), Number(clock[2]));
  const compact = /^(\d{3,4})$/.exec(text);
  if (compact) {
    const digits = compact[1].padStart(4, '0');
    return militaryTime(Number(digits.slice(0, 2)), Number(digits.slice(2)));
  }
  return undefined;
}

function militaryTime(hour: number, minute: number): string | undefined {
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return undefined;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

function TimeColumn({
  label,
  options,
  selected,
  onSelect,
}: {
  label: string;
  options: string[];
  selected: string;
  onSelect: (value: string) => void;
}) {
  const listRef = React.useRef<HTMLDivElement>(null);
  const selectedRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    const list = listRef.current;
    const selectedButton = selectedRef.current;
    if (!list || !selectedButton) return;
    list.scrollTop =
      selectedButton.offsetTop -
      list.clientHeight / 2 +
      selectedButton.clientHeight / 2;
  }, [selected]);

  return (
    <div className="tw:flex tw:w-16 tw:flex-col">
      <div className="tw:px-2 tw:py-2 tw:text-center tw:text-xs tw:font-medium tw:text-muted-foreground">
        {label}
      </div>
      <div ref={listRef} className="tw:h-56 tw:overflow-y-auto tw:px-1 tw:pb-1">
        {options.map((option) => (
          <Button
            key={option}
            ref={option === selected ? selectedRef : undefined}
            type="button"
            variant={option === selected ? 'default' : 'ghost'}
            size="sm"
            className="tw:w-full tw:justify-center tw:font-normal tw:tabular-nums"
            onClick={() => onSelect(option)}
          >
            {option}
          </Button>
        ))}
      </div>
    </div>
  );
}

export { TimePicker };
